import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type TabId = 'home' | 'pass' | 'loot' | 'more';
const TAB_IDS: TabId[] = ['home', 'pass', 'loot', 'more'];

// Old ?panel= deep links (QR codes, bookmarks, wallet passes) keep working:
// every game-shell panel now lives on a tab. Loot + lore live on the LOOT
// tab (own & spend); quests + badges on PASS (progress).
const LEGACY_PANEL_TO_TAB: Record<string, TabId> = {
  loot: 'loot', quests: 'pass', badges: 'pass', lore: 'loot', pass: 'pass', more: 'more',
  // Bean Snake (Task 14) enters from HOME, not a tab — this only routes the
  // ?panel=arcade deep link (QR code, push) to the right TAB; Dashboard is
  // what actually reads the raw `panel` param to open the ArcadePanel
  // overlay on top of it.
  arcade: 'home',
};

// The tab shipped as ?tab=rewards for one day (2026-08-05) before the LOOT
// rename — keep those links honest too.
const LEGACY_TAB_ALIASES: Record<string, TabId> = { rewards: 'loot' };

export function useTabParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const legacy = searchParams.get('panel');
  const rawTab = searchParams.get('tab');
  const raw =
    (rawTab ? LEGACY_TAB_ALIASES[rawTab] ?? rawTab : null) ??
    (legacy ? LEGACY_PANEL_TO_TAB[legacy] ?? null : null);
  const requested: TabId = raw && (TAB_IDS as string[]).includes(raw) ? (raw as TabId) : 'home';
  const activeTab: TabId = requested;

  const selectTab = useCallback((t: TabId) => {
    const next = new URLSearchParams(searchParams);
    next.delete('panel');
    if (t === 'home') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true }); // tabs replace — back exits the app, not the tab
  }, [searchParams, setSearchParams]);

  return { activeTab, selectTab };
}
